import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { api } from './api';

export interface MathCaptchaChallenge {
  question: string;
  token: string;
}

/**
 * A fresh math-captcha challenge (see apps/api/src/http/captcha.ts).
 *
 * Stateless and single-use server-side, so this must never come from a stale
 * cache — `staleTime`/`gcTime` are both 0, and callers `refetch()` whenever a
 * token gets used (successfully or not) rather than reusing one.
 */
export function useMathCaptcha(enabled = true): UseQueryResult<MathCaptchaChallenge> {
  return useQuery({
    queryKey: ['captcha-math'],
    queryFn: () => api.get<MathCaptchaChallenge>('/captcha/math'),
    enabled,
    staleTime: 0,
    gcTime: 0,
  });
}

const DEVANAGARI_DIGITS = '०१२३४५६७८९';

/** "17 - 14" -> "१७ - १४", for displaying the challenge in Marathi. */
export function toDevanagariDigits(text: string): string {
  return text.replace(/[0-9]/g, (d) => DEVANAGARI_DIGITS[Number(d)]!);
}

/**
 * Parse a captcha answer that may be typed in Devanagari digits (natural once
 * the question itself is shown that way) back into a plain number — the
 * server only ever compares against Arabic-numeral arithmetic.
 */
export function parseLocalizedNumber(text: string): number {
  const normalized = text.replace(/[०-९]/g, (d) => String(DEVANAGARI_DIGITS.indexOf(d)));
  return Number(normalized);
}
